import { api } from './apiClient';
import { mockTemplates, MOCK_CONFIG, isUsingMockData, addMockMetadata } from './mockTemplates';
import { logger } from './logger';

// Inicializar configuración al cargar el módulo
(async () => {
  try {
    await MOCK_CONFIG.initialize();
  } catch (error) {
    logger.warn('Error inicializando configuración de mock:', { error });
  }
})();

const TEMPLATES_ENDPOINT = '/api/templates';

const templateService = {
  // Función helper para determinar si usar datos mock
  shouldUseMockData: () => {
    return MOCK_CONFIG.enabled && process.env.NODE_ENV === 'development';
  },

  // Función helper para logging de fallback
  logFallbackUsage: (method, reason) => {
    if (MOCK_CONFIG.logFallback) {
      logger.warn(`Usando datos mock en ${method}: ${reason}`, {
        component: 'templateService',
        method,
        reason,
        mockConfig: {
          enabled: MOCK_CONFIG.enabled,
          env: process.env.NODE_ENV
        }
      });
    }
  },

  getTemplates: async () => {
    try {
      logger.info('Obteniendo lista de plantillas desde API', {
        component: 'templateService',
        method: 'getTemplates',
        endpoint: TEMPLATES_ENDPOINT
      });

      // Usar apiClient para incluir automáticamente el token JWT
      const data = await api.get(TEMPLATES_ENDPOINT);

      logger.info('Datos JSON parseados', {
        component: 'templateService',
        method: 'getTemplates',
        dataType: typeof data,
        isArray: Array.isArray(data),
        dataLength: Array.isArray(data) ? data.length : 'N/A',
        firstItem: Array.isArray(data) && data.length > 0 ? data[0] : 'N/A'
      });

      // Mapear campos del backend al formato esperado por el frontend (asegurar lowercase)
      const mappedData = Array.isArray(data) ? data.map(template => ({
        ...template, // Mantener todas las propiedades originales
        // Asegurar versiones lowercase existan
        id: template.id || template.Id,
        name: template.name || template.Name,
        description: template.description || template.Description,
        category: template.category || template.Category,
        company: template.company || template.Company,
        createdBy: template.createdBy || template.CreatedBy,
        createdAt: template.createdAt || template.CreatedAt,
        lastModifiedBy: template.lastModifiedBy || template.LastModifiedBy,
        lastModifiedAt: template.lastModifiedAt || template.LastModifiedAt,
        isActive: template.isActive !== undefined ? template.isActive : template.IsActive,
        validationRulesCount: template.validationRulesCount || template.ValidationRulesCount,
        gdbStructureJson: template.gdbStructureJson || template.GdbStructureJson,
        // Campos calculados
        status: template.status || template.Status || 
          ((template.isActive !== undefined ? template.isActive : template.IsActive) ? 'active' : 'inactive'),
        lastModified: (template.lastModifiedAt || template.LastModifiedAt) ? new Date(template.lastModifiedAt || template.LastModifiedAt).toLocaleDateString('es-ES') : 'N/A',
        gdbStructure: template.gdbStructureJson || template.GdbStructureJson
      })) : data;

      logger.info('Datos mapeados', {
        component: 'templateService',
        method: 'getTemplates',
        mappedLength: Array.isArray(mappedData) ? mappedData.length : 'N/A',
        firstMappedItem: Array.isArray(mappedData) && mappedData.length > 0 ? {
          id: mappedData[0].id,
          name: mappedData[0].name,
          status: mappedData[0].status,
          createdBy: mappedData[0].createdBy
        } : 'N/A'
      });

      return mappedData;
    } catch (error) {
      logger.error('Error obteniendo plantillas con fetch directo', {
        component: 'templateService',
        method: 'getTemplates',
        error: error.message,
        errorName: error.name
      });

      // Solo usar datos mock si está habilitado y es un error de conectividad
      if (templateService.shouldUseMockData()) {
        templateService.logFallbackUsage('getTemplates', `Error de red o backend no disponible: ${error.message}`);
        return addMockMetadata(mockTemplates);
      }

      // Re-lanzar error si no podemos usar fallback
      throw error;
    }
  },

  getTemplate: async (id) => {
    try {
      logger.info('Obteniendo plantilla específica desde API', {
        component: 'templateService',
        method: 'getTemplate',
        templateId: id,
        endpoint: `${TEMPLATES_ENDPOINT}/${id}`
      });

      // Usar apiClient para incluir automáticamente el token JWT
      const data = await api.get(`${TEMPLATES_ENDPOINT}/${id}`);

      logger.info('Datos JSON parseados para template específico', {
        component: 'templateService',
        method: 'getTemplate',
        dataType: typeof data,
        hasData: !!data,
        templateId: data?.id,
        templateName: data?.name,
        hasGdbStructure: !!data?.gdbStructureJson,
        gdbStructureLength: data?.gdbStructureJson?.length || 'N/A'
      });

      // Mapear campos del backend al formato esperado por el frontend (consistente con getTemplates)
      const mappedData = data ? {
        ...data,
        status: (data.isActive !== undefined ? data.isActive : data.IsActive) ? 'active' : 'inactive',
        lastModified: (data.lastModifiedAt || data.LastModifiedAt) ? new Date(data.lastModifiedAt || data.LastModifiedAt).toLocaleDateString('es-ES') : 'N/A',
        gdbStructure: data.gdbStructureJson || data.GdbStructureJson // Mapear GdbStructureJson a gdbStructure
      } : null;

      logger.info('Datos mapeados para template específico', {
        component: 'templateService',
        method: 'getTemplate',
        mappedDataExists: !!mappedData,
        finalGdbStructureLength: mappedData?.gdbStructure?.length || 'N/A',
        templateId: id
      });

      return mappedData;
    } catch (error) {
      logger.error('Error obteniendo plantilla específica', {
        component: 'templateService',
        method: 'getTemplate',
        templateId: id,
        error: error.message,
        errorName: error.name
      });

      // Solo usar datos mock si está habilitado y es un error de conectividad
      if (templateService.shouldUseMockData()) {
        templateService.logFallbackUsage('getTemplate', `Error de red o backend no disponible para ID ${id}: ${error.message}`);
        const mockData = mockTemplates.find(t => t.id === parseInt(id)) || null;
        return mockData ? addMockMetadata([mockData])[0] : null;
      }

      // Re-lanzar error si no podemos usar fallback
      throw error;
    }
  },

  createTemplate: async (formData) => {
    try {
      logger.debug('createTemplate - formData', { formData });
      logger.debug('createTemplate - formData.category', { category: formData.category });

      logger.info(`Creando nueva plantilla: ${formData.name}`, {
        component: 'templateService',
        method: 'createTemplate',
        templateName: formData.name,
        category: formData.category,
        hasGdbFile: !!formData.gdbFile
      });

      // Si hay archivo GDB, usar endpoint combinado que procesa todo en un paso
      if (formData.gdbFile) {
        logger.info(`Usando endpoint combinado para crear plantilla con GDB: ${formData.name}`, {
          component: 'templateService',
          method: 'createTemplate',
          endpoint: `${TEMPLATES_ENDPOINT}/upload`
        });

        const uploadData = new FormData();
        uploadData.append('name', formData.name);
        uploadData.append('description', formData.description || '');
        uploadData.append('category', formData.category || '');
        uploadData.append('status', formData.status || 'active');
        uploadData.append('gdbFile', formData.gdbFile);

        const result = await api.post(`${TEMPLATES_ENDPOINT}/upload`, uploadData);

        logger.info(`Plantilla completa creada exitosamente con GDB procesado: ${formData.name}`, {
          component: 'templateService',
          method: 'createTemplate',
          templateName: formData.name,
          templateId: result?.data?.id,
          hasGdbStructure: !!result?.data?.gdbStructureJson
        });

        return result;
      } else {
        // Si no hay archivo GDB, crear plantilla básica
        logger.info(`Creando plantilla básica sin GDB: ${formData.name}`, {
          component: 'templateService',
          method: 'createTemplate',
          endpoint: TEMPLATES_ENDPOINT
        });

        const createData = {
          name: formData.name,
          description: formData.description,
          category: formData.category,
          status: formData.status || 'active'
        };

        const result = await api.post(TEMPLATES_ENDPOINT, createData);

        logger.info(`Plantilla básica creada exitosamente: ${formData.name}`, {
          component: 'templateService',
          method: 'createTemplate',
          templateName: formData.name,
          templateId: result?.data?.id
        });

        return result;
      }
    } catch (error) {
      logger.error(`Error creando plantilla: ${formData.name}`, {
        component: 'templateService',
        method: 'createTemplate',
        templateName: formData.name,
        hasGdbFile: !!formData.gdbFile,
        error: error.message
      });
      throw error;
    }
  },

  uploadGdbFile: async (templateId, gdbFile) => {
    try {
      logger.info(`Subiendo archivo GDB para plantilla ${templateId}`, {
        component: 'templateService',
        method: 'uploadGdbFile',
        templateId,
        fileName: gdbFile.name
      });

      const data = new FormData();
      data.append('templateId', templateId.toString());
      data.append('gdbFile', gdbFile);

      const result = await api.post(`${TEMPLATES_ENDPOINT}/upload-gdb`, data);

      logger.info(`Archivo GDB subido exitosamente para plantilla ${templateId}`, {
        component: 'templateService',
        method: 'uploadGdbFile',
        templateId
      });

      return result;
    } catch (error) {
      logger.error(`Error subiendo archivo GDB para plantilla ${templateId}`, {
        component: 'templateService',
        method: 'uploadGdbFile',
        templateId,
        error: error.message
      });
      throw error;
    }
  },

  updateTemplate: async (id, templateData) => {
    try {
      logger.info(`Actualizando plantilla ${id}`, {
        component: 'templateService',
        method: 'updateTemplate',
        templateId: id,
        templateName: templateData.name
      });

      const result = await api.put(`${TEMPLATES_ENDPOINT}/${id}`, templateData);

      logger.info(`Plantilla ${id} actualizada exitosamente`, {
        component: 'templateService',
        method: 'updateTemplate',
        templateId: id
      });

      return result;
    } catch (error) {
      logger.error(`Error actualizando plantilla ${id}`, {
        component: 'templateService',
        method: 'updateTemplate',
        templateId: id,
        error: error.message
      });
      throw error;
    }
  },

  deleteTemplate: async (id, options = {}) => {
    try {
      logger.info(`Eliminando plantilla ${id}`, {
        component: 'templateService',
        method: 'deleteTemplate',
        templateId: id,
        force: options.force
      });

      // El backend requiere un body con DeactivatedBy para el método DeactivateTemplate
      const deleteData = {
        DeactivatedBy: options.deactivatedBy || 'system' // Usar el usuario actual o 'system' por defecto
      };

      const params = new URLSearchParams();
      if (options.force) {
        params.append('force', 'true');
      }
      const queryString = params.toString();
      const url = queryString ? `${TEMPLATES_ENDPOINT}/${id}?${queryString}` : `${TEMPLATES_ENDPOINT}/${id}`;
      const result = await api.del(url, { body: deleteData });

      logger.info(`Plantilla ${id} eliminada exitosamente`, {
        component: 'templateService',
        method: 'deleteTemplate',
        templateId: id
      });

      return result;
    } catch (error) {
      logger.error(`Error eliminando plantilla ${id}`, {
        component: 'templateService',
        method: 'deleteTemplate',
        templateId: id,
        error: error.message
      });
      throw error;
    }
  },

  cloneTemplate: async (id, cloneData) => {
    try {
      logger.info(`Clonando plantilla ${id} con nombre: ${cloneData.name}`, {
        component: 'templateService',
        method: 'cloneTemplate',
        sourceTemplateId: id,
        newName: cloneData.name
      });

      const result = await api.post(`${TEMPLATES_ENDPOINT}/clone/${id}`, {
        NewName: cloneData.name,
        Description: cloneData.description,
        Category: cloneData.category
      });

      logger.info(`Plantilla ${id} clonada exitosamente como: ${cloneData.name}`, {
        component: 'templateService',
        method: 'cloneTemplate',
        sourceTemplateId: id,
        newName: cloneData.name
      });

      return result;
    } catch (error) {
      logger.error(`Error clonando plantilla ${id}`, {
        component: 'templateService',
        method: 'cloneTemplate',
        sourceTemplateId: id,
        newName: cloneData.name,
        error: error.message
      });
      throw error;
    }
  },
};

export default templateService;
